import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchDataFromApi } from '../../helpers/api';
import { getCurrentToken } from '../../utility/getCurrentToken';
import { logout } from '../slices/authSlice';
import { resetInstances } from './instancesSlice';
import { resetImages } from './imagesSlice';
import { resetOverview } from './overviewSlice';
import axiosInstance from '../../utility/axiosInstance';
import {handleAxiosError} from "../../helpers/handleAxiosError";
interface Flavor {
    /** Set when this size needs approval on this account. The row renders disabled with the reason. */
    restricted?: boolean;
    restricted_reason?: string;
    id: string;
    name: string;
    vcpus: string;
    ram: string;
    disk: string;
    [key: string]: any;
}

interface FlavorsState {
    data: Flavor[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: FlavorsState = {
    data: [],
    status: 'idle',
    error: null,
};

export const fetchFlavors = createAsyncThunk(
    'flavors/fetchFlavors',
    async (_, { rejectWithValue }) => {
        try {
            // include_restricted: the console SHOWS the approval-only sizes greyed out with the
            // reason, so a user can see the size exists and learn how to ask for it. The API omits
            // them by default, which is what an agent caller gets: it never sees an id it cannot
            // use. Same endpoint, two audiences. See backend middlewares/restrictedFlavors.ts.
            const response = await axiosInstance.get('/v1/flavors?include_restricted=1');
            return response.data;
        } catch (error) {
            console.error("Failed to fetch flavors:", error);
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const flavorsSlice = createSlice({
    name: 'flavors',
    initialState,
    reducers: {
        resetFlavors: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFlavors.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchFlavors.fulfilled, (state, action: PayloadAction<Flavor[]>) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchFlavors.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });
    },
});

export const { resetFlavors } = flavorsSlice.actions;
export default flavorsSlice.reducer;